import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { pakkuIcon, LocalStorageKeys } from 'javascript/Constants'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import userSync from 'javascript/helpers/userSync'
import { checkImage } from 'Utilities'
import { useSettingsStore } from 'Store/settings'

export const useAppStore = defineStore('App', () => {
  const settingsStore = useSettingsStore()

  const windowLocation = ref('')
  const baseURL = ref('')
  const imageURL = ref(pakkuIcon)
  const imageReady = ref(false)
  const websiteFacilityCode = ref('')
  const inputMessage = ref('')
  const hideSignIn = ref(false)
  const isFullScreen = ref(false)
  const isChatWindowOpened = ref(false)
  const isOnBookingWidget = ref(false)
  const isMinimized = ref(triplaLocalStorage.getItem('triplabot-icon-minimized') === 'true')
  const latestMessageID = ref(null)
  const autoMessageAuthId = ref(null)
  const autoMessageAuthType = ref(null)
  const emailConfirmationStatus = ref('')
  const iframesHaveLoadedCSS = ref([])
  const session = ref(null)
  const chatChannel = ref(triplaLocalStorage.getItem(LocalStorageKeys.SELECTED_CHAT_CHANNEL))

  // getters
  const conciergeIconStyle = computed(() => ({ backgroundImage: `url(${imageURL.value})` }))
  const isEmailConfirmationDone = computed(() => emailConfirmationStatus.value === 'done')

  function setChatChannel(value) {
    chatChannel.value = value
    triplaLocalStorage.setItem(LocalStorageKeys.SELECTED_CHAT_CHANNEL, value)
  }

  function setLatestMessageID(value) {
    latestMessageID.value = value
  }

  function setWindowLocation(value) {
    windowLocation.value = value
  }

  function setIsOnBookingWidget(value) {
    isOnBookingWidget.value = value
  }

  function setBaseURL(value) {
    baseURL.value = value
  }

  function setIsFullScreen(value) {
    isFullScreen.value = value
  }

  function setWebsiteFacilityCode(value) {
    websiteFacilityCode.value = value
  }

  function setAutoMessageAuthId(value) {
    autoMessageAuthId.value = value
  }

  function setAutoMessageAuthType(value) {
    autoMessageAuthType.value = value
  }

  function setChatWindowOpened(value) {
    isChatWindowOpened.value = value
  }

  function setHideSignIn(value) {
    hideSignIn.value = value
  }

  function setMinimized(value) {
    if (isMinimized.value !== value) {
      userSync.setState({ isMinimized: value })
    }

    isMinimized.value = value
    triplaLocalStorage.setItem('triplabot-icon-minimized', value)
  }

  function setImageReady(value) {
    imageReady.value = value
  }

  function setSession(value) {
    session.value = value
  }

  function setInputMessage(value) {
    inputMessage.value = value
  }

  function setEmailConfirmationStatus(value) {
    emailConfirmationStatus.value = value
  }

  function setImageURL(value) {
    checkImage(value, pakkuIcon).then((image) => {
      imageURL.value = value
    })
  }

  function appendIframeHasLoadedCSS(iframeName) {
    iframesHaveLoadedCSS.value = [...iframesHaveLoadedCSS.value, iframeName]
  }

  function resetIframeHaveLoadedCSS() {
    iframesHaveLoadedCSS.value = []
  }

  function openChatChannel(channel) {
    const channelData = settingsStore.settings?.chatbot_channel_settings?.find(
      (c) => c.channel_type === channel
    )
    if (channelData) {
      window.open(
        channelData.settings.url.startsWith('http')
          ? channelData.settings.url
          : `https://${channelData.settings.url}`,
        '_blank'
      )
    }
  }

  return {
    windowLocation,
    baseURL,
    imageURL,
    imageReady,
    websiteFacilityCode,
    inputMessage,
    hideSignIn,
    isFullScreen,
    isChatWindowOpened,
    isOnBookingWidget,
    isMinimized,
    latestMessageID,
    autoMessageAuthId,
    autoMessageAuthType,
    emailConfirmationStatus,
    iframesHaveLoadedCSS,
    session,
    chatChannel,

    // getters
    conciergeIconStyle,
    isEmailConfirmationDone,

    setBaseURL,
    setChatChannel,
    setLatestMessageID,
    setWindowLocation,
    setIsOnBookingWidget,
    setIsFullScreen,
    setAutoMessageAuthId,
    setAutoMessageAuthType,
    setWebsiteFacilityCode,
    appendIframeHasLoadedCSS,
    setChatWindowOpened,
    setHideSignIn,
    setMinimized,
    setImageReady,
    setInputMessage,
    setEmailConfirmationStatus,
    resetIframeHaveLoadedCSS,
    setImageURL,
    setSession,
    openChatChannel
  }
})
